import { z } from 'zod';
const id = z.number().int().positive();
const name = z.string().trim().min(1).max(200);
const description = z.string().max(4000);
const note = z.string().max(2000);
const confirm = z.string();
const includeThumbnails = z.boolean().optional();
const tagIds = z.array(id).max(50);
export const EVENT_FORMATS = ['in person', 'online', 'hybrid', 'pre-read', 'other'];
export const UPLOAD_PURPOSES = ['deck_import', 'slide_version', 'template_version'];
export const TAG_TARGETS = ['slide', 'group', 'presentation'];
export const HISTORY_TARGETS = ['project', 'slide', 'group', 'presentation', 'template', 'event'];
export const WHERE_USED_TARGETS = ['slide', 'group', 'template'];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the form YYYY-MM-DD.');
const clockTime = z.string().regex(/^\d{2}:\d{2}$/, 'Use the form HH:MM.');
export const chapterInput = z.object({
    title: z.string().trim().min(1).max(200),
    numbered: z.boolean().default(true),
    group_ids: z.array(id).max(500),
});
export const importUnit = z.object({
    pages: z.array(z.number().int().positive()).min(1),
    title: z.string().trim().min(1).max(200).optional(),
});
export const operationInputs = {
    whoami: z.object({}),
    list_projects: z.object({ include_archived: z.boolean().optional() }),
    get_project: z.object({ project_id: id }),
    list_tags: z.object({ project_id: id }),
    search_slides: z.object({
        project_id: id,
        query: z.string().max(200).optional(),
        tag_ids: tagIds.optional(),
        include_retired: z.boolean().optional(),
        sort: z.enum(['title', 'last_change']).optional(),
    }),
    get_slide: z.object({ slide_id: id, include_thumbnails: includeThumbnails }),
    list_slide_versions: z.object({ slide_id: id }),
    list_groups: z.object({ project_id: id, tag_ids: tagIds.optional(), include_retired: z.boolean().optional() }),
    get_group: z.object({ group_id: id, include_thumbnails: includeThumbnails }),
    list_presentations: z.object({
        project_id: id,
        tag_ids: tagIds.optional(),
        include_retired: z.boolean().optional(),
    }),
    get_presentation: z.object({ presentation_id: id }),
    where_used: z.object({ target: z.enum(WHERE_USED_TARGETS), id }),
    list_generations: z.object({ presentation_id: id }),
    get_template: z.object({ project_id: id, include_thumbnails: includeThumbnails }),
    list_events: z.object({ project_id: id }),
    get_event: z.object({ event_id: id }),
    get_import: z.object({ import_id: id, include_thumbnails: includeThumbnails }),
    get_history: z.object({
        target: z.enum(HISTORY_TARGETS),
        id,
        limit: z.number().int().positive().max(500).optional(),
    }),
    get_download_url: z.object({
        kind: z.enum(['generation', 'slide_version', 'group', 'template_version']),
        id,
    }),
    create_project: z.object({ name, description: description.optional() }),
    update_project: z.object({ project_id: id, name: name.optional(), description: description.optional() }),
    archive_project: z.object({ project_id: id, archived: z.boolean() }),
    create_tag: z.object({ project_id: id, name: z.string().trim().min(1).max(40) }),
    update_tag: z.object({ tag_id: id, name: z.string().trim().min(1).max(40) }),
    delete_tag: z.object({ tag_id: id, confirm }),
    set_tags: z.object({ target: z.enum(TAG_TARGETS), ids: z.array(id).min(1).max(500), tag_ids: tagIds }),
    request_upload: z.object({ purpose: z.enum(UPLOAD_PURPOSES) }),
    start_import: z.object({ project_id: id, upload_id: id, filename: z.string().trim().min(1).max(255).optional() }),
    commit_import: z.object({
        import_id: id,
        units: z.array(importUnit).optional(),
        new_groups: z
            .array(z.object({ name, description: description.optional(), units: z.array(z.number().int().min(0)).min(1) }))
            .optional(),
        append_to_groups: z
            .array(z.object({ group_id: id, units: z.array(z.number().int().min(0)).min(1) }))
            .optional(),
    }),
    discard_import: z.object({ import_id: id, confirm }),
    create_slide_version: z.object({
        slide_id: id,
        upload_id: id,
        note: note.optional(),
        event_id: id.optional(),
    }),
    update_slide: z.object({ slide_id: id, title: name }),
    retire_slide: z.object({ slide_id: id, confirm }),
    unretire_slide: z.object({ slide_id: id }),
    create_group: z.object({
        project_id: id,
        name,
        description: description.optional(),
        slide_ids: z.array(id).max(500).optional(),
    }),
    update_group: z.object({ group_id: id, name: name.optional(), description: description.optional() }),
    set_group_slides: z.object({
        group_id: id,
        base_version: z.number().int().min(0),
        slide_ids: z.array(id).max(500),
        note: note.optional(),
    }),
    retire_group: z.object({ group_id: id, confirm }),
    unretire_group: z.object({ group_id: id }),
    create_presentation: z.object({
        project_id: id,
        name,
        description: description.optional(),
        show_agenda: z.boolean().optional(),
        show_dividers: z.boolean().optional(),
    }),
    update_presentation: z.object({
        presentation_id: id,
        name: name.optional(),
        description: description.optional(),
        show_agenda: z.boolean().optional(),
        show_dividers: z.boolean().optional(),
    }),
    set_presentation_structure: z.object({
        presentation_id: id,
        base_revision: z.number().int().positive(),
        chapters: z.array(chapterInput).max(200),
    }),
    copy_presentation: z.object({ presentation_id: id }),
    retire_presentation: z.object({ presentation_id: id, confirm }),
    unretire_presentation: z.object({ presentation_id: id }),
    create_template_version: z.object({ project_id: id, upload_id: id, note: note.optional() }),
    copy_template: z.object({ project_id: id, target_project_id: id }),
    generate_presentation: z.object({ presentation_id: id }),
    regenerate: z.object({ generation_id: id }),
    regenerate_affected: z.object({ target: z.enum(WHERE_USED_TARGETS), id }),
    create_event: z.object({
        project_id: id,
        title: name,
        date: isoDate,
        start_time: clockTime.nullable().optional(),
        format: z.enum(EVENT_FORMATS),
        location: z.string().max(500).optional(),
        audience: z.string().max(1000).optional(),
        attendees: z.array(z.string().trim().min(1).max(200)).max(200).optional(),
        notes: z.string().max(10000).optional(),
        presentation_ids: z.array(id).max(50).optional(),
    }),
    update_event: z.object({
        event_id: id,
        title: name.optional(),
        date: isoDate.optional(),
        start_time: clockTime.nullable().optional(),
        format: z.enum(EVENT_FORMATS).optional(),
        location: z.string().max(500).optional(),
        audience: z.string().max(1000).optional(),
        attendees: z.array(z.string().trim().min(1).max(200)).max(200).optional(),
        notes: z.string().max(10000).optional(),
    }),
    set_event_presentations: z.object({ event_id: id, presentation_ids: z.array(id).min(1).max(50) }),
    generate_event: z.object({ event_id: id }),
    delete_event: z.object({ event_id: id, confirm }),
};
export const READ_OPERATIONS = [
    'whoami', 'list_projects', 'get_project', 'list_tags', 'search_slides', 'get_slide',
    'list_slide_versions', 'list_groups', 'get_group', 'list_presentations', 'get_presentation',
    'where_used', 'list_generations', 'get_template', 'list_events', 'get_event', 'get_import',
    'get_history', 'get_download_url',
];
export const WRITE_OPERATIONS = [
    'create_project', 'update_project', 'archive_project', 'create_tag', 'update_tag', 'delete_tag',
    'set_tags', 'request_upload', 'start_import', 'commit_import', 'discard_import',
    'create_slide_version', 'update_slide', 'retire_slide', 'unretire_slide', 'create_group',
    'update_group', 'set_group_slides', 'retire_group', 'unretire_group', 'create_presentation',
    'update_presentation', 'set_presentation_structure', 'copy_presentation', 'retire_presentation',
    'unretire_presentation', 'create_template_version', 'copy_template', 'generate_presentation',
    'regenerate', 'regenerate_affected', 'create_event', 'update_event', 'set_event_presentations',
    'generate_event', 'delete_event',
];
//# sourceMappingURL=operations.js.map